import type { VFrameBase } from '@festina/life-general-types/life'
import { useAuthenticationContext } from '@festina-life/authentication'
import {
  type Except,
  isEmpty,
  type Merge,
  type Optional,
} from '@festina-life/core'
import { Alert } from '@festina-life/ui/feedback'
import {
  FormikForm,
  type FormikFormProps,
  isYupObjectSchema,
} from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import { ExistingValuesProvider, useDataContext } from '@festina-life/ui/theme'
import type { FormikValues } from 'formik'
import { useMemo } from 'react'

import { LifeFrameUpdater } from './LifeFrameUpdater'
import type { LifeStatusType } from './useLifeFormikContext'
import { createYupSchema } from './yup/createValidation'

type LifeFormBaseProps<
  T extends object,
  F extends LifeStatusType['frame'],
> = Merge<
  Except<FormikFormProps<T>, 'initialValues'>,
  {
    onSubmit?: (values: T) => void
    frame: F
    initialValues: FormikFormProps<T>['initialValues']
    existingValues?: T
  }
>

export type LifeFormProps<
  T extends object,
  F extends LifeStatusType['frame'],
> = LifeFormBaseProps<T, F> &
  (
    | {
        customVFrameGetter: ({
          name,
          frame,
          values,
        }: {
          name: string
          frame: F
          values: T
        }) => Optional<VFrameBase>
        frameHash: number
      }
    | {
        customVFrameGetter?: never
        frameHash?: never
      }
  )

/**
 *
 * @beta we have some un-resolved issues/questions regarding cleanup of values APG2223-2417
 */
export const LifeForm = <
  T extends FormikValues,
  F extends LifeStatusType['frame'],
>({
  frame,
  initialValues,
  children,
  validationSchema,
  existingValues,
  onSubmit,
  onInputChange,
  shouldFieldsBeDisabled = false,
  customVFrameGetter,
  frameHash,
  // Spread is used to not forget to pass down props.
  ...other
}: LifeFormProps<T, F>) => {
  const translate = useTranslate()
  const { isInTestMode } = useAuthenticationContext()
  const { shouldSkipFrame } = useDataContext()

  const lifeFormValidationSchema = useMemo(() => {
    const frameValidation =
      frame && !shouldSkipFrame
        ? createYupSchema({
            frame,
            translate,
            isInTestMode,
          })
        : null

    return isYupObjectSchema(validationSchema) &&
      isYupObjectSchema(frameValidation)
      ? validationSchema.concat(frameValidation)
      : (frameValidation ?? validationSchema)
  }, [frame, isInTestMode, translate, validationSchema, shouldSkipFrame])

  const isMissingFrame = !shouldSkipFrame && isEmpty(frame)

  return (
    <>
      {isMissingFrame && (
        <Alert severity="error">{translate('form.missingFrame.warning')}</Alert>
      )}
      <FormikForm<T>
        validationSchema={
          // shouldFieldsBeDisabled is only provided if we are reading in a state that should not
          // have any validation. Because else we might suddenly be hit by some new validation on an old command or frames change
          shouldFieldsBeDisabled ? undefined : lifeFormValidationSchema
        }
        initialStatus={{
          frame,
          hasFormValidated: false,
          fieldVFrame: () => ({ _type: '' }),
        }}
        initialValues={initialValues}
        onSubmit={onSubmit}
        onInputChange={onInputChange}
        shouldFieldsBeDisabled={shouldFieldsBeDisabled}
        {...other}
      >
        {({ resetForm, ...formikProps }) => (
          <ExistingValuesProvider<T> existingValues={existingValues}>
            <LifeFrameUpdater<T, F>
              frame={frame ?? { _type: '' }}
              customVFrameGetter={customVFrameGetter}
              frameHash={frameHash}
            >
              {typeof children === 'function'
                ? children({
                    ...formikProps,
                    resetForm: (formikState) =>
                      resetForm({ ...formikState, status: formikProps.status }), // We don't want to reset the status to the initial one, but keep the current one.
                  })
                : children}
            </LifeFrameUpdater>
          </ExistingValuesProvider>
        )}
      </FormikForm>
    </>
  )
}
